import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { RankSeries } from '../../api/types'
import { formatDate, formatDateTime, formatTime } from '../../lib/format'
import { mergeRankSeries, seriesKey } from '../../lib/rank-series'

export interface Marker {
	at: string
	label: string
}

interface Props {
	series: RankSeries[]
	labelFor(s: RankSeries): string
	colorFor(s: RankSeries): string
	markers?: Marker[]
	end?: number
	height?: number
}

/** Step lines of rank over time with rank 1 at the top; gaps where the app was out of the chart. */
export function RankChart({ series, labelFor, colorFor, markers = [], end = Date.now(), height = 320 }: Props) {
	const rows = mergeRankSeries(series, end)
	if (rows.length < 2) return <div className="empty">Not enough ranking history yet.</div>
	const first = rows[0].t
	const short = end - first < 2 * 86_400_000
	return (
		<ResponsiveContainer width="100%" height={height}>
			<LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
				<CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
				<XAxis
					dataKey="t"
					type="number"
					scale="time"
					domain={[first, end]}
					tickFormatter={(t) => (short ? formatTime(new Date(t)) : formatDate(new Date(t)))}
					stroke="var(--text-3)"
					fontSize={12}
				/>
				<YAxis
					reversed
					domain={[1, (max: number) => max + 1]}
					allowDecimals={false}
					stroke="var(--text-3)"
					fontSize={12}
					width={40}
				/>
				<Tooltip
					labelFormatter={(t) => formatDateTime(new Date(Number(t)))}
					formatter={(value) => (value === null ? 'not ranked' : `#${value}`)}
					contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
				/>
				{markers
					.filter((m) => new Date(m.at).getTime() >= first)
					.map((m) => (
						<ReferenceLine
							key={m.at + m.label}
							x={new Date(m.at).getTime()}
							stroke="var(--text-3)"
							strokeDasharray="2 4"
							label={{ value: m.label, position: 'insideTopLeft', fontSize: 11, fill: 'var(--text-2)' }}
						/>
					))}
				{series.map((s) => (
					<Line
						key={seriesKey(s)}
						dataKey={seriesKey(s)}
						name={labelFor(s)}
						type="stepAfter"
						stroke={colorFor(s)}
						strokeWidth={2}
						dot={false}
						connectNulls={false}
						isAnimationActive={false}
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	)
}
